import React, { useState, useEffect } from 'react';
import { Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useCreateRentMutation } from '../../slices/rentsApiSlice';
import { useGetHousesQuery } from '../../slices/housesApiSlice';
import { useGetTenantsQuery } from '../../slices/tenantsApiSlice';
import { useGetLandlordsQuery } from '../../slices/landlordsApiSlice';

const AddStaffRent = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    house: '',
    tenant: '',
    landlord: '',
    startDate: '',
    endDate: '',
    lastRentEnd: '',
    monthlyRent: '',
    numberOfMonths: 1,
    totalRent: '',
    address: '',
    recordedAt: '',
    datePaid: '',
    remark: '',
    images: [],
  });

  const [houseFilter, setHouseFilter] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');

  const [createRent, { isLoading, error }] = useCreateRentMutation();
  const { data: housesData } = useGetHousesQuery({ keyword: houseFilter });
  const { data: tenantsData } = useGetTenantsQuery({ keyword: tenantFilter });
  const { data: landlordsData } = useGetLandlordsQuery({ keyword: '' });

  const houses = housesData?.houses || [];
  const tenants = tenantsData?.tenants || [];
  const landlords = landlordsData?.landlords || [];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: ['monthlyRent', 'numberOfMonths'].includes(name) ? Number(value) : value,
    }));
  };

  // Auto-fill fields when tenant is selected
  useEffect(() => {
    const selectedTenant = tenants.find((t) => t._id === formData.tenant);
    if (selectedTenant) {
      const houseId = selectedTenant.house?._id || selectedTenant.house;
      const selectedHouse = houses.find((h) => h._id === houseId);
      setFormData((prev) => ({
        ...prev,
        house: houseId || '',
        landlord: selectedHouse?.landlord?._id || selectedTenant.landlord?._id || '',
        monthlyRent: selectedTenant.monthlyRent || '',
        address: selectedTenant.address || '',
        numberOfMonths: 1,
      }));
    }
  }, [formData.tenant, tenants, houses]);

  const validateFiles = (files) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    const maxSize = 5 * 1024 * 1024;
    for (let file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`Unsupported file type: ${file.name}`);
        return false;
      }
      if (file.size > maxSize) {
        toast.error(`File too large: ${file.name}`);
        return false;
      }
    }
    return true;
  };

  const handleFileUpload = async (files) => {
    if (!validateFiles(files)) return;
    const uploadData = new FormData();
    Array.from(files).forEach((file) => uploadData.append('images', file));
    try {
      const res = await fetch('/api/upload', { method: 'POST', body: uploadData });
      const data = await res.json();
      if (res.ok) {
        setFormData((prev) => ({
          ...prev,
          images: [...prev.images, ...data.images.map((img) => img.path || img)].filter(Boolean),
        }));
        toast.success('Images uploaded');
      } else {
        throw new Error(data.message || 'Upload failed');
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    const totalRent = formData.monthlyRent * formData.numberOfMonths;

    if (!totalRent || totalRent <= 0) {
      toast.error('Total rent must be greater than 0');
      return;
    }

    const payload = {
      ...formData,
      totalRent,
    };

    try {
      await createRent(payload).unwrap();
      toast.success('Rent record added');
      navigate('/rentlistscreen');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to add rent');
    }
  };

  return (
    <div className="container mt-4">
      <h1>Add New Rent Record</h1>
      <Form onSubmit={submitHandler}>
        {/* House Filter */}
        <Form.Group className="mb-3">
          <Form.Label>Filter Houses</Form.Label>
          <Form.Control
            type="text"
            placeholder="Search houses..."
            value={houseFilter}
            onChange={(e) => setHouseFilter(e.target.value)}
          />
        </Form.Group>

        {/* House (auto-filled) */}
        <Form.Group className="mb-3" controlId="house">
          <Form.Label>House</Form.Label>
          <Form.Select name="house" value={formData.house} disabled>
            <option value="">Select House</option>
            {houses.map((h) => (
              <option key={h._id} value={h._id}>
                {h.name} - {h.address}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {/* Tenant Filter */}
        <Form.Group className="mb-3">
          <Form.Label>Filter Tenants</Form.Label>
          <Form.Control
            type="text"
            placeholder="Search tenants..."
            value={tenantFilter}
            onChange={(e) => setTenantFilter(e.target.value)}
          />
        </Form.Group>

        {/* Tenant */}
        <Form.Group className="mb-3" controlId="tenant">
          <Form.Label>Tenant</Form.Label>
          <Form.Select name="tenant" value={formData.tenant} onChange={handleChange} required>
            <option value="">Select Tenant</option>
            {tenants.map((t) => (
              <option key={t._id} value={t._id}>
                {t.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {/* Landlord (auto-filled) */}
        <Form.Group className="mb-3" controlId="landlord">
          <Form.Label>Landlord</Form.Label>
          <Form.Select name="landlord" value={formData.landlord} disabled>
            <option value="">Select Landlord</option>
            {landlords.map((l) => (
              <option key={l._id} value={l._id}>
                {l.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {/* Dates */}
        <Form.Group className="mb-3" controlId="startDate">
          <Form.Label>Start Date</Form.Label>
          <Form.Control type="date" name="startDate" value={formData.startDate} onChange={handleChange} required />
        </Form.Group>

        <Form.Group className="mb-3" controlId="endDate">
          <Form.Label>End Date</Form.Label>
          <Form.Control type="date" name="endDate" value={formData.endDate} onChange={handleChange} required />
        </Form.Group>

        <Form.Group className="mb-3" controlId="lastRentEnd">
          <Form.Label>Last Rent End</Form.Label>
          <Form.Control type="date" name="lastRentEnd" value={formData.lastRentEnd} onChange={handleChange} />
        </Form.Group>

        {/* Rent Details */}
        <Form.Group className="mb-3" controlId="monthlyRent">
          <Form.Label>Monthly Rent</Form.Label>
          <Form.Control type="number" name="monthlyRent" value={formData.monthlyRent} onChange={handleChange} required />
        </Form.Group>

        <Form.Group className="mb-3" controlId="numberOfMonths">
          <Form.Label>Number of Months</Form.Label>
          <Form.Control type="number" name="numberOfMonths" value={formData.numberOfMonths} onChange={handleChange} required />
        </Form.Group>

        <Form.Group className="mb-3" controlId="totalRent">
          <Form.Label>Total Rent</Form.Label>
          <Form.Control type="number" value={formData.monthlyRent * formData.numberOfMonths || ''} readOnly />
        </Form.Group>

                <Form.Group className="mb-3" controlId="address">
          <Form.Label>Tenant Address</Form.Label>
          <Form.Control
            type="text"
            name="address"
            value={formData.address}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="recordedAt">
          <Form.Label>Recorded At</Form.Label>
          <Form.Control
            type="date"
            name="recordedAt"
            value={formData.recordedAt}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="datePaid">
          <Form.Label>Date Paid</Form.Label>
          <Form.Control
            type="date"
            name="datePaid"
            value={formData.datePaid}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="remark">
          <Form.Label>Remark</Form.Label>
          <Form.Control
            as="textarea"
            name="remark"
            value={formData.remark}
            onChange={handleChange}
            rows={3}
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="images">
          <Form.Label>Upload Rent Images</Form.Label>
          <Form.Control
            type="file"
            multiple
            accept="image/*"
            onChange={(e) => handleFileUpload(e.target.files)}
          />
          <div className="mt-3 d-flex flex-wrap gap-2">
            {formData.images.map((img, idx) => (
              <div
                key={idx}
                style={{
                  position: 'relative',
                  width: '100px',
                  height: '100px',
                  marginRight: '10px',
                }}
              >
                <img
                  src={img}
                  alt={`Uploaded ${idx}`}
                  className="img-fluid"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '4px',
                  }}
                />
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => removeImage(idx)}
                  style={{
                    position: 'absolute',
                    top: '0',
                    right: '0',
                    borderRadius: '50%',
                    padding: '2px 6px',
                    lineHeight: '1',
                  }}
                >
                  ×
                </Button>
              </div>
            ))}
          </div>
        </Form.Group>

        <Button type="submit" variant="primary" disabled={isLoading}>
          {isLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Adding Rent...
            </>
          ) : (
            'Add Rent'
          )}
        </Button>

        {error && (
          <Alert variant="danger" className="mt-3">
            {error?.data?.message || 'Error adding rent record'}
          </Alert>
        )}
      </Form>
    </div>
  );
};

export default AddStaffRent;